<template>
  <div>
    <div class="modal" id="memorandum-detail-modal">
      <div class="modal-dialog modal-fullscreen" role="document">
        <div class="modal-content">

          <div class="modal-header">
            <div class="d-flex align-items-center" style="min-width:0; flex:1;">
              <span class="memo-type-badge mr-2" :class="'type-' + (memo.type || 'office')">
                {{ memo.type === 'hei' ? 'HEI Memo' : 'Office Memo' }}
              </span>
              <h5 class="modal-title font-weight-bold text-truncate mb-0">
                {{ memo.title }}
              </h5>
            </div>
            <div class="d-flex align-items-center ml-3 flex-shrink-0">
              <div v-if="canManage" class="mode-toggle mr-3" role="group">
                <button
                  type="button"
                  class="mode-btn"
                  :class="{ active: mode === 'viewer' }"
                  @click="mode = 'viewer'"
                >
                  <i class="bi bi-eye mr-1"></i> Viewer
                </button>
                <button
                  type="button"
                  class="mode-btn"
                  :class="{ active: mode === 'admin' }"
                  @click="mode = 'admin'"
                >
                  <i class="bi bi-pencil-square mr-1"></i> Admin
                </button>
              </div>
              <button type="button" class="close ml-0" data-dismiss="modal" aria-label="Close">
                <span style="font-size:28px;">&times;</span>
              </button>
            </div>
          </div>

          <div class="modal-body p-0" style="overflow:hidden;">
            <div class="row no-gutters" style="height:calc(100vh - 80px);">

              <!-- ── Left panel: details ── -->
              <div class="col-lg-4 col-xl-3 d-flex flex-column detail-panel" style="height:calc(100vh - 80px); min-height:0;">
                <div class="overflow-auto flex-grow-1 px-3 py-3" style="min-height:0;">

                  <table class="table table-borderless table-sm detail-table mb-3">
                    <tbody>
                      <tr>
                        <td class="detail-label">Memo No.</td>
                        <td class="detail-value">
                          <span class="memo-number-badge">
                            No. {{ String(memo.number || 0).padStart(3, '0') }} s. {{ seriesYear }}
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <td class="detail-label">Type</td>
                        <td class="detail-value">
                          {{ memo.type === 'hei' ? 'HEI Memorandum' : 'Office Memorandum' }}
                        </td>
                      </tr>
                      <tr>
                        <td class="detail-label">Date Issued</td>
                        <td class="detail-value">{{ memo.date_issued | shortDate }}</td>
                      </tr>
                      <tr>
                        <td class="detail-label">Recipient</td>
                        <td class="detail-value">{{ memo.recipient || '—' }}</td>
                      </tr>
                    </tbody>
                  </table>

                  <!-- Admin-mode actions -->
                  <template v-if="mode === 'admin' && canManage">
                    <hr class="my-2" />
                    <div class="d-flex flex-column" style="gap:.5rem;">
                      <button class="btn btn-outline-primary btn-sm w-100" @click="$emit('edit', memo)">
                        <i class="bi bi-pencil mr-1"></i> Edit Memorandum
                      </button>
                      <button class="btn btn-outline-danger btn-sm w-100" :disabled="deleting" @click="confirmDelete">
                        <span v-if="deleting" class="spinner-border spinner-border-sm mr-1"></span>
                        <i v-else class="bi bi-trash mr-1"></i> Delete Memorandum
                      </button>
                    </div>
                  </template>

                  <!-- View original link -->
                  <div class="mt-3" v-if="memo.path">
                    <a
                      :href="'https://drive.google.com/file/d/' + memo.path + '/view'"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="small"
                    >
                      <i class="bi bi-box-arrow-up-right mr-1"></i>Open in Google Drive
                    </a>
                  </div>

                </div>
              </div>

              <!-- ── Right panel: PDF viewer ── -->
              <div class="col-lg-8 col-xl-9 d-flex flex-column" style="height:calc(100vh - 80px); border-left:1px solid rgba(0,0,21,.08);">
                <div v-if="!memo.path" class="flex-grow-1 d-flex align-items-center justify-content-center text-muted">
                  <div class="text-center">
                    <i class="bi bi-file-earmark-x" style="font-size:2.5rem; opacity:.35;"></i>
                    <p class="mt-2 mb-0">No PDF uploaded for this memorandum.</p>
                  </div>
                </div>
                <iframe
                  v-else
                  :src="'https://drive.google.com/file/d/' + memo.path + '/preview'"
                  width="100%"
                  style="flex:1; min-height:0; border:none;"
                  allowfullscreen
                ></iframe>
              </div>

            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "MemorandumDetailModal",
  filters: {
    shortDate(value) {
      if (!value) return "—";
      return new Date(value).toLocaleDateString("en-PH", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
  },
  data() {
    return {
      memo: {
        id: null,
        number: null,
        title: "",
        type: "office",
        recipient: "",
        date_issued: null,
        path: "",
      },
      mode: "viewer",
      deleting: false,
    };
  },
  computed: {
    canManage() {
      const role = this.$store.state.user && this.$store.state.user.role;
      return ["Administrator", "Chief", "RD"].includes(role);
    },
    seriesYear() {
      if (!this.memo.date_issued) return "—";
      return new Date(this.memo.date_issued).getFullYear();
    },
  },
  methods: {
    show(memo) {
      this.memo = { ...memo };
      this.mode = "viewer";
      this.deleting = false;
      $("#memorandum-detail-modal").modal("show");
    },
    confirmDelete() {
      const label = `Memorandum No. ${String(this.memo.number || 0).padStart(3, "0")} s. ${this.seriesYear}`;
      if (!confirm(`Delete ${label}?\n\nThis cannot be undone.`)) return;
      this.deleting = true;
      axios
        .delete(`api/memorandums/${this.memo.id}`)
        .then((res) => {
          toastr.success((res.data && res.data.message) || "Deleted successfully.", "Information");
          $("#memorandum-detail-modal").modal("hide");
          this.$emit("deleted");
        })
        .catch(() => {
          toastr.error("Failed to delete memorandum.", "Error");
        })
        .finally(() => {
          this.deleting = false;
        });
    },
  },
  mounted() {
    $("#memorandum-detail-modal").on("hidden.bs.modal", () => {
      this.memo = {
        id: null, number: null, title: "", type: "office",
        recipient: "", date_issued: null, path: "",
      };
      this.mode = "viewer";
    });
  },
};
</script>

<style scoped>
.modal-fullscreen {
  max-width: 100%;
  margin: 0;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  height: 100vh;
  display: flex;
  position: fixed;
  z-index: 100000;
}

.detail-panel { background: #fff; }

.detail-table td { padding: .35rem .5rem; vertical-align: top; }
.detail-label {
  font-size: .72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .35px;
  color: #768192;
  white-space: nowrap;
  width: 100px;
}
.detail-value { font-size: .88rem; color: #2b3a55; }

.memo-number-badge {
  display: inline-block;
  padding: .22rem .55rem;
  border-radius: 999px;
  background: rgba(77,141,255,.1);
  color: #4d8dff;
  font-size: .78rem;
  font-weight: 700;
}

/* ── Mode toggle ── */
.mode-toggle {
  display: inline-flex;
  border: 1px solid rgba(0,0,21,.14);
  border-radius: 10px;
  overflow: hidden;
}
.mode-btn {
  background: transparent;
  border: none;
  padding: .3rem .75rem;
  cursor: pointer;
  font-size: .8rem;
  color: #768192;
  transition: background .15s, color .15s;
  line-height: 1.4;
}
.mode-btn.active { background: #4f46e5; color: #fff; }
.mode-btn:not(.active):hover { background: rgba(0,0,21,.05); }

.memo-type-badge {
  display: inline-block;
  padding: .22rem .55rem;
  border-radius: 999px;
  font-size: .68rem;
  font-weight: 800;
  letter-spacing: .4px;
  flex-shrink: 0;
}
.memo-type-badge.type-office { background: rgba(43,182,163,.12); color: #1f8f7f; }
.memo-type-badge.type-hei    { background: rgba(224,144,31,.12);  color: #c97812; }
</style>
